/**
 * Where the account pages live, and how they name their subject.
 *
 * Every page about a team names it as `?account=<id>`, for the reason every page here names its subject
 * in the query string rather than the path: no address then carries a value a reader has to know the
 * shape of. The sections share the parameter, so moving between them keeps the team.
 */

/**
 * Every account this person is part of. Where a switch between them is made, and the landing with several.
 *
 * "Team" is the business term on screen and in the address, for the teams a person creates and their
 * own personal one alike. "Account" is the word in the code only.
 */
export const ACCOUNTS_PATH = '/teams';

export const ACCOUNT_PARAM = 'account';

/** The developer's own page. Under their name, because it is about them and not about any account. */
export const PROFILE_PATH = '/profile';

/**
 * The pages one team has, in the order the sidebar offers them.
 *
 * Overview is for everybody who is part of the team: an owner reads the team's state there, a member
 * reads their own seat's. The rest are the owner's - the API answers 404 to anybody else - and are not
 * offered to a member at all.
 *
 * A personal team is the free tier and nothing else: nobody is invited into it, no seat is bought on it,
 * and it keeps the name it was given. So it has the three pages marked `personal` and the others open
 * its overview instead - see `sectionsOf`.
 */
export const SECTIONS = [
   { id: 'overview', label: 'Overview', icon: 'fa-solid fa-gauge', owner: false, personal: true },
   { id: 'members', label: 'Members', icon: 'fa-solid fa-users', owner: true, personal: false },
   { id: 'repositories', label: 'Repositories', icon: 'fa-brands fa-git-alt', owner: true, personal: true },
   { id: 'tokens', label: 'Project tokens', icon: 'fa-solid fa-key', owner: true, personal: true },
   { id: 'billing', label: 'Billing', icon: 'fa-solid fa-credit-card', owner: true, personal: false },
   { id: 'settings', label: 'Settings', icon: 'fa-solid fa-gear', owner: true, personal: false }
];

/** A page of one account: a section, or a page under one such as `members/owners`. */
export function accountPath(page, accountId)
{
   return `/${page}?${ACCOUNT_PARAM}=${encodeURIComponent(accountId)}`;
}

/** The section a pathname belongs to - its own page or one underneath it - or null for anything else. */
export function sectionOf(pathname)
{
   return SECTIONS.find(section => pathname === `/${section.id}` || pathname.startsWith(`/${section.id}/`)) ?? null;
}

/** Whether a team is the person's own, as the list marks it or as the owner's view names its kind. */
export function isPersonal(account)
{
   return Boolean(account?.personal || account?.kind === 'personal');
}

/**
 * The sections one team offers the person reading it: three for their personal team, every one for a
 * team they own, the overview alone for a team they merely hold a seat in.
 */
export function sectionsOf(account)
{
   return SECTIONS.filter(section => (isPersonal(account)
      ? section.personal
      : !section.owner || account.owner || account.unknown));
}

/** The icon a team reads as: the person for their own, the group for one they created or joined. */
export function accountIcon(account)
{
   return isPersonal(account) ? 'fa-solid fa-user' : 'fa-solid fa-people-group';
}

/** A snake-cased value as a word, for the handful of states these pages show. */
export function word(value)
{
   const text = String(value ?? '').replaceAll('_', ' ');

   return text ? text[0].toUpperCase() + text.slice(1) : '—';
}

/**
 * The colour a seat or plan state reads as, to the person paying for it.
 *
 * Quieter than the operator's palette: green for what is fine, amber only for what needs their attention,
 * grey for what is over.
 */
export function stateVariant(state)
{
   switch (state)
   {
   case 'active':
   case 'public':
      return 'success';
   case 'pending':
   case 'past_due':
   case 'trialing':
      return 'warning';
   case 'reclaimed':
   case 'cancelled':
      return 'secondary';
   default:
      return 'secondary';
   }
}
